import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { getMotif } from '../../data/typeMotifs';

// 5軸の位置 (上から時計回り、Pentagon と同じ順序)
// activation / judgment / choice / resonance / awareness
const AXIS_KEYS = ['activation', 'judgment', 'choice', 'resonance', 'awareness'];

const vertexOf = (axisKey, radius) => {
  const idx = AXIS_KEYS.indexOf(axisKey);
  const angle = (Math.PI * 2 * idx) / 5 - Math.PI / 2;
  return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
};

const twinkle = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
`;

const Glyph = styled.text`
  font-family: 'Apple Color Emoji', 'Segoe UI Symbol', 'Noto Sans Symbols 2',
               'Noto Sans Symbols', ${p => p.theme.fonts.display}, serif;
  font-size: ${p => p.$size}px;
  fill: ${p => p.$color};
  text-anchor: middle;
  dominant-baseline: central;
  font-weight: 500;
`;

const Star = styled.circle`
  animation: ${twinkle} 5s ease-in-out infinite;
  animation-delay: ${p => p.$delay}s;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/**
 * StarChart — 24type 固有のシンボル
 * 中心: 代表天体グリフ (♂♀☉☽☿♃♄♅♆⛤✦)
 * 周辺: 5軸の方向に星を配置。主軸の方向ほど大きく明るい。
 *
 * @param {Object} props
 * @param {string} props.code - 4文字 type code
 * @param {number} props.size - SVG 辺長 (px)
 * @param {string} props.color - 星と glyph の主色 (デフォルト 黄金)
 * @param {boolean} props.faint - 全体の透過率を下げる装飾モード
 */
const StarChart = ({
  code = '',
  size = 120,
  color = '#E6C77E',
  faint = false,
}) => {
  const motif = useMemo(() => getMotif(code), [code]);

  // 5軸位置 (radius = viewBox 0..100 の中で外側に配置)
  const center = 50;
  const orbitR = 36;
  const glyphFont = 28;

  // 軸ごとの強さ: primaryAxes の順序から決まる (前ほど強い)
  const axisStrength = useMemo(() => {
    const map = {};
    AXIS_KEYS.forEach(k => { map[k] = 0.25; });  // base
    motif.primaryAxes.forEach((k, i) => {
      // 1番目=1.0, 2番目=0.65, 3番目以降=0.45
      map[k] = i === 0 ? 1.0 : i === 1 ? 0.65 : 0.45;
    });
    return map;
  }, [motif]);

  return (
    <Svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
      {/* 軸の星 */}
      {AXIS_KEYS.map((axis, i) => {
        const strength = axisStrength[axis];
        const v = vertexOf(axis, orbitR);
        const cx = center + v.x;
        const cy = center + v.y;
        const r = 1.0 + strength * 2.4;        // 1.0〜3.4
        const opacity = (faint ? 0.4 : 0.95) * (0.35 + strength * 0.65);
        return (
          <Star
            key={axis}
            cx={cx}
            cy={cy}
            r={r}
            fill={color}
            opacity={opacity}
            $delay={i * 0.6}
          />
        );
      })}

      {/* 中心の天体グリフ */}
      <Glyph
        x={center}
        y={center + 1}
        $size={glyphFont}
        $color={color}
        opacity={faint ? 0.6 : 0.92}
      >
        {motif.glyph}
      </Glyph>
    </Svg>
  );
};

export default StarChart;
